import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Radio, Headphones, Mic2, Globe, Users, Zap, Play, Pause, Volume2, VolumeX, Check, ChevronRight, ChevronLeft, Crown, ExternalLink, Music, Upload, Search, CheckCircle, XCircle } from "lucide-react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import AuthModal from "../components/AuthModal";
import SEO from "../components/SEO";
import audioBus from "../services/AudioBus";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const DJsAndMCMediaHero = ({ onOpenAuth }) => {
  const [nowPlaying, setNowPlaying] = useState(null);
  const [levels, setLevels] = useState(new Array(48).fill(10));
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.3); // Start at 30% volume
  const [audioContextStarted, setAudioContextStarted] = useState(false);
  const { loginWithGoogle } = useAuth();

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const audioBusHandle = useRef(null);
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);

  // Ensure video plays (fallback for browsers that block autoplay)
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.play().catch(() => {});
    }
  }, []);

  // Set initial volume when audio element is ready
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
        audioRef.current.muted = false;
      }
    }
  };

  useEffect(() => {
    async function fetchNowPlaying() {
      try {
        const res = await fetch(`${BACKEND_URL}/api/nowplaying/wnuv`);
        const data = await res.json();
        setNowPlaying({
          title: data.now_playing.song.title,
          artist: data.now_playing.song.artist,
          art: data.now_playing.song.art,
          listeners: data.listeners.current,
        });
      } catch (err) {
        console.log("Now playing fetch failed", err);
      }
    }
    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 15000);
    return () => clearInterval(interval);
  }, []);

  const initAudioContext = () => {
    if (audioContextStarted || !audioRef.current) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 128;
      const source = audioContext.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(audioContext.destination);
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      setAudioContextStarted(true);
      function animate() {
        analyser.getByteFrequencyData(dataArray);
        const newLevels = Array.from({ length: 48 }).map((_, i) => {
          const value = dataArray[i] || 0;
          return Math.min(80, Math.max(8, value * 0.3));
        });
        setLevels(newLevels);
        animationRef.current = requestAnimationFrame(animate);
      }
      animate();
    } catch (err) {
      console.log("Audio context init failed", err);
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (!audioContextStarted) initAudioContext();
    if (audioContextRef.current?.state === 'suspended') audioContextRef.current.resume();
    if (isPlaying) { audioRef.current.pause(); }
    else {
      if (!audioBusHandle.current) {
        audioBusHandle.current = audioBus.register('home-hero', {
          pause: () => { if (audioRef.current) audioRef.current.pause(); setIsPlaying(false); }
        });
      }
      audioBusHandle.current.claim();
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setIsMuted(!isMuted);
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioBusHandle.current) audioBusHandle.current.unregister();
    };
  }, []);

  return (
    <div id="home" className="min-h-screen flex items-center justify-center bg-cover bg-center relative overflow-hidden pt-28 pb-20"
      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=2000')" }}>
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay muted loop playsInline
        className="absolute inset-0 w-full h-full object-cover z-0"
        poster="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=2000"
      >
        <source src="/hero-video.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/70 z-[1]" />
      
      {/* Floating Music Notes — dense scatter with gentle bobbing */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        {[
          // Quarter notes
          { x: '3%', y: '10%', size: 32, delay: 0, dur: 8, opacity: 0.35, type: 'quarter' },
          { x: '14%', y: '60%', size: 26, delay: 2.5, dur: 10, opacity: 0.25, type: 'quarter' },
          { x: '33%', y: '6%', size: 38, delay: 1.2, dur: 9, opacity: 0.30, type: 'quarter' },
          { x: '53%', y: '72%', size: 24, delay: 4.0, dur: 11, opacity: 0.22, type: 'quarter' },
          { x: '76%', y: '18%', size: 30, delay: 0.8, dur: 7, opacity: 0.32, type: 'quarter' },
          { x: '91%', y: '52%', size: 28, delay: 3.5, dur: 9, opacity: 0.26, type: 'quarter' },
          { x: '46%', y: '38%', size: 22, delay: 5.0, dur: 12, opacity: 0.18, type: 'quarter' },
          { x: '66%', y: '82%', size: 34, delay: 1.8, dur: 8, opacity: 0.28, type: 'quarter' },
          // Eighth notes
          { x: '8%', y: '32%', size: 34, delay: 0.5, dur: 9, opacity: 0.38, type: 'eighth' },
          { x: '23%', y: '78%', size: 28, delay: 3.0, dur: 10, opacity: 0.24, type: 'eighth' },
          { x: '40%', y: '15%', size: 30, delay: 1.5, dur: 8, opacity: 0.33, type: 'eighth' },
          { x: '58%', y: '48%', size: 24, delay: 4.5, dur: 11, opacity: 0.20, type: 'eighth' },
          { x: '83%', y: '28%', size: 38, delay: 2.0, dur: 7, opacity: 0.30, type: 'eighth' },
          { x: '70%', y: '66%', size: 26, delay: 0.3, dur: 10, opacity: 0.23, type: 'eighth' },
          { x: '1%', y: '52%', size: 32, delay: 5.5, dur: 9, opacity: 0.28, type: 'eighth' },
          { x: '94%', y: '8%', size: 24, delay: 2.8, dur: 12, opacity: 0.20, type: 'eighth' },
          // Double eighth notes
          { x: '18%', y: '42%', size: 40, delay: 1.0, dur: 10, opacity: 0.35, type: 'double' },
          { x: '48%', y: '3%', size: 34, delay: 3.8, dur: 8, opacity: 0.28, type: 'double' },
          { x: '73%', y: '56%', size: 32, delay: 0.6, dur: 9, opacity: 0.32, type: 'double' },
          { x: '36%', y: '88%', size: 36, delay: 2.2, dur: 11, opacity: 0.24, type: 'double' },
          { x: '86%', y: '40%', size: 28, delay: 4.2, dur: 7, opacity: 0.22, type: 'double' },
          { x: '6%', y: '75%', size: 30, delay: 5.2, dur: 10, opacity: 0.26, type: 'double' },
          // Treble clefs (larger, elegant accents)
          { x: '28%', y: '22%', size: 48, delay: 2.0, dur: 14, opacity: 0.20, type: 'treble' },
          { x: '63%', y: '12%', size: 42, delay: 4.8, dur: 16, opacity: 0.16, type: 'treble' },
          { x: '80%', y: '72%', size: 44, delay: 0.4, dur: 15, opacity: 0.18, type: 'treble' },
          { x: '12%', y: '88%', size: 40, delay: 6.5, dur: 13, opacity: 0.15, type: 'treble' },
          // Extra fill
          { x: '55%', y: '28%', size: 26, delay: 3.2, dur: 9, opacity: 0.22, type: 'eighth' },
          { x: '42%', y: '58%', size: 30, delay: 1.4, dur: 8, opacity: 0.26, type: 'quarter' },
          { x: '88%', y: '85%', size: 24, delay: 5.8, dur: 11, opacity: 0.20, type: 'eighth' },
          { x: '0%', y: '20%', size: 28, delay: 0.2, dur: 10, opacity: 0.24, type: 'double' },
          { x: '60%', y: '92%', size: 32, delay: 4.0, dur: 9, opacity: 0.30, type: 'quarter' },
          { x: '96%', y: '35%', size: 22, delay: 7.0, dur: 10, opacity: 0.18, type: 'eighth' },
          { x: '22%', y: '4%', size: 20, delay: 1.8, dur: 11, opacity: 0.22, type: 'quarter' },
        ].map((note, i) => (
          <svg
            key={i}
            className="absolute"
            style={{
              left: note.x,
              top: note.y,
              width: `${note.size}px`,
              height: `${note.size * 1.6}px`,
              opacity: note.opacity,
              animation: `noteFloat${i % 3} ${note.dur}s ease-in-out ${note.delay}s infinite`,
              filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.5)) drop-shadow(0 0 20px rgba(168, 85, 247, 0.15))',
            }}
            viewBox="0 0 24 40"
          >
            {note.type === 'quarter' && (
              <g>
                <ellipse cx="8" cy="34" rx="6" ry="4.5" transform="rotate(-20 8 34)" fill="#c4b5fd" />
                <rect x="13" y="6" width="2.2" height="28" rx="1" fill="#c4b5fd" />
              </g>
            )}
            {note.type === 'eighth' && (
              <g>
                <ellipse cx="8" cy="34" rx="6" ry="4.5" transform="rotate(-20 8 34)" fill="#c4b5fd" />
                <rect x="13" y="6" width="2.2" height="28" rx="1" fill="#c4b5fd" />
                <path d="M15.2,6 C18,8 22,12 20,19 C21,14 18,10 15.2,9" fill="#c4b5fd" />
              </g>
            )}
            {note.type === 'double' && (
              <g>
                <ellipse cx="5" cy="34" rx="4.5" ry="3.5" transform="rotate(-20 5 34)" fill="#c4b5fd" />
                <ellipse cx="17" cy="30" rx="4.5" ry="3.5" transform="rotate(-20 17 30)" fill="#c4b5fd" />
                <rect x="9" y="8" width="2" height="26" rx="1" fill="#c4b5fd" />
                <rect x="21" y="4" width="2" height="26" rx="1" fill="#c4b5fd" />
                <rect x="9" y="5.5" width="14" height="2.8" rx="1" fill="#c4b5fd" />
              </g>
            )}
            {note.type === 'treble' && (
              <path d="M12,2 C12,2 7,7 7,13 C7,17 10,19 12,19 C13,19 10,23 10,27 C10,31 13,35 16,35 C20,35 21,31 18,29 C16,27 13,28 13,31 M12,19 C14,17 19,12 17,7 C15,3 12,2 12,2 M12,19 L12,39"
                stroke="#c4b5fd" strokeWidth="1.8" fill="none" strokeLinecap="round" />
            )}
          </svg>
        ))}
      </div>

      {/* ORIGINAL CLOUDS (commented out — uncomment to restore)
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]">
        <div className="absolute w-[200%] h-full animate-clouds-slow flex items-start gap-32 pt-4">
          <svg className="w-[400px] h-[120px] text-white/20 flex-shrink-0" viewBox="0 0 400 120" fill="currentColor">
            <path d="M50,90 Q30,90 25,75 Q15,75 15,60 Q15,45 35,40 Q35,25 55,25 Q70,10 95,15 Q115,5 140,15 Q160,10 180,20 Q200,15 220,25 Q245,15 270,25 Q290,20 310,35 Q335,25 355,40 Q375,35 385,55 Q395,55 395,70 Q395,85 375,90 Z"/>
          </svg>
        </div>
      </div>
      */}

      {/* Audio Reactive Waveform */}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center gap-[4px] h-24 opacity-70 pointer-events-none z-[2]">
        {levels.map((h, i) => (
          <div key={i} style={{ height: `${h}px`, width: "6px", background: "linear-gradient(to top, #a855f7, #ec4899)", borderRadius: "4px 4px 0 0", transition: "height 0.1s linear" }} />
        ))}
      </div>

      <div className="relative text-center text-white max-w-3xl px-6 z-[3]">
        <h2 className="text-sm tracking-widest opacity-80 mb-4 font-medium" data-testid="brand-tagline">DJ'S AND MC MEDIA BROADCAST NETWORK</h2>
        <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-testid="hero-headline">The Shopify for Radio Creators</h1>
        <p className="text-lg opacity-90 mb-3 max-w-2xl mx-auto" data-testid="hero-subheadline">Launch your channel. Build your audience. Monetize your sound — without running your own station.</p>
        <p className="text-sm opacity-70 mb-8 max-w-2xl mx-auto" data-testid="hero-tagline">No servers. No licensing headaches. Just go live.</p>
        <p className="text-xs opacity-40 mb-8 max-w-2xl mx-auto tracking-wide" data-testid="hero-built-for">Built for DJs, artists, and creators.</p>

        <div className="flex flex-wrap gap-4 justify-center mb-12">
          <a href="#pricing" className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-8 py-4 rounded-2xl shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2" data-testid="start-station-btn">
            <Radio className="w-5 h-5" /> Start Your Channel
          </a>
          <button className="bg-white/90 hover:bg-white text-black px-8 py-4 rounded-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 font-semibold" data-testid="listen-live-btn" onClick={handlePlayPause}>
            <Headphones className="w-5 h-5" /> Listen Live
          </button>
        </div>

        {/* Now Playing Card with Glow Effect */}
        <div className="relative max-w-xl mx-auto">
          {/* Animated Glow Effect */}
          <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 rounded-3xl blur-xl opacity-20 animate-glow"></div>
          
          <div className="relative bg-black/60 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/10" data-testid="now-playing-card">
            <p className="text-sm opacity-80 mb-4 flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Now Playing – The NU Vibe
            </p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="w-28 h-28 bg-white/10 rounded-2xl overflow-hidden flex items-center justify-center text-xs shadow-lg">
                {nowPlaying?.art ? <img src={nowPlaying.art} alt="album art" className="w-full h-full object-cover" data-testid="album-art" /> : <div className="flex flex-col items-center justify-center text-white/50"><Radio className="w-8 h-8 mb-1" /><span>Album Art</span></div>}
              </div>
              <div className="text-left">
                <p className="font-bold text-xl" data-testid="track-title">{nowPlaying?.title || "Current Track"}</p>
                <p className="opacity-80 text-sm mt-1" data-testid="track-artist">{nowPlaying?.artist || "Artist Name"}</p>
                <div className="flex items-center gap-2 text-xs opacity-70 mt-3">
                  <Users className="w-4 h-4" />
                  <span data-testid="listener-count">{nowPlaying?.listeners ?? "--"} listeners</span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-center gap-4">
              <button onClick={handlePlayPause} className="w-14 h-14 bg-purple-500 hover:bg-purple-600 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg" data-testid="play-pause-btn">
                {isPlaying ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-1" />}
              </button>
            </div>
            
            {/* Volume Slider */}
            <div className="mt-4 flex items-center justify-center gap-3 px-4">
              <button onClick={handleMute} className="hover:text-purple-400 transition" data-testid="mute-btn">
                {isMuted || volume === 0 ? <VolumeX className="w-4 h-4 text-white/50 hover:text-purple-400" /> : <Volume2 className="w-4 h-4 text-white/50 hover:text-purple-400" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-32 h-2 bg-white/20 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-4
                  [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:bg-purple-500
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:hover:bg-purple-400
                  [&::-webkit-slider-thumb]:transition-colors"
                data-testid="volume-slider"
              />
              <span className="text-white/50 text-xs w-8">{Math.round(volume * 100)}%</span>
            </div>
            
            <audio ref={audioRef} crossOrigin="anonymous" className="hidden">
              <source src="https://djsandmc.media/listen/wnuv/radio.mp3" type="audio/mpeg" />
            </audio>
          </div>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-3 gap-6 mt-14 text-sm opacity-90" data-testid="platform-stats">
          <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
            <p className="text-3xl font-bold text-purple-400">24/7</p>
            <p className="mt-1 text-white/70">AutoDJ Broadcast</p>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
            <p className="text-3xl font-bold text-pink-400">Global</p>
            <p className="mt-1 text-white/70">DJ Network</p>
          </div>
          <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
            <p className="text-3xl font-bold text-purple-400">Unlimited</p>
            <p className="mt-1 text-white/70">Station Growth</p>
          </div>
        </div>
      </div>
    </div>
  );
};


const FeaturesSection = () => {
  const navigate = useNavigate();
  const features = [
    { icon: <Mic2 className="w-8 h-8" />, title: "Live Channel Streaming", description: "Go live anytime with professional streaming tools. Connect your DJ software and stream to thousands." },
    { icon: <Radio className="w-8 h-8" />, title: "AutoDJ System", description: "Keep your channel running 24/7 with intelligent playlist scheduling and smooth transitions." },
    { icon: <Globe className="w-8 h-8" />, title: "Music Licensing Included", description: "Stream legally from day one. ASCAP licensed — with more PROs being added as the network grows." },
    { icon: <Zap className="w-8 h-8" />, title: "Launch Your Channel Instantly", description: "Launch your channel in minutes. No technical knowledge required. We handle the infrastructure." }
  ];

  return (
    <section id="features" className="py-24 bg-gradient-to-b from-[#0f0f1a] to-[#13112e]" data-testid="features-section">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">Everything You Need to Build Your Channel</h2>
        <p className="text-center text-white/60 mb-16 max-w-2xl mx-auto">Channel hosting, licensing, and creator tools — all in one platform.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div key={index} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-purple-500/50" data-testid={`feature-card-${index}`}>
              <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
        
        {/* DJ's & MC Live Promo */}
        <div className="mt-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Pro Experience Upgrade</h3>
          <p className="text-white/60 mb-6 max-w-xl mx-auto">
            See what your channel could look like with DJ's & MC Live - our flagship radio player with live chat, 
            song requests, and more.
          </p>
          <button 
            onClick={() => navigate('/features')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 rounded-xl transition inline-flex items-center gap-2"
          >
            <ExternalLink className="w-5 h-5" />
            Explore Player Features
          </button>
        </div>
      </div>
    </section>
  );
};


const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "DJsandMCMedia made it so easy to launch my channel. I was live in under an hour — no server setup, no headaches.",
      name: "DJ Smooth",
      role: "Starter Channel Operator",
      accent: "purple",
    },
    {
      quote: "The licensing being handled by the platform is a game changer. I can just focus on my music and my audience.",
      name: "MC Vibe",
      role: "Pro Channel Operator",
      accent: "green",
    },
    {
      quote: "I submitted a track and got featured within a week. Already seeing plays from listeners I never would have reached.",
      name: "Lex Beats",
      role: "Independent Artist",
      accent: "yellow",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-[#13112e] to-[#13112e]" data-testid="testimonials-section">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">Creators Love the Platform</h2>
        <p className="text-center text-white/50 mb-12 max-w-xl mx-auto">Join a growing network of DJs, artists, and creators building their audience on DJsandMCMedia.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.05] transition" data-testid={`testimonial-${i}`}>
              <div className={`text-4xl mb-4 ${t.accent === 'green' ? 'text-green-400' : t.accent === 'yellow' ? 'text-yellow-400' : 'text-purple-400'}`}>"</div>
              <p className="text-white/80 text-sm leading-relaxed mb-6">{t.quote}</p>
              <div>
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-white/40 text-xs">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


const ArtistsSection = () => {
  return (
    <section id="artists" className="py-20 bg-gradient-to-b from-[#13112e] via-[#0d0d1a] to-[#0a0a14]">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div>
            <span className="inline-block bg-green-500/20 text-green-400 text-sm font-semibold px-4 py-1 rounded-full mb-4">FOR ARTISTS</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Upload Your Music.<br />
              <span className="text-green-400">Get Played. Get Paid.</span>
            </h2>
            <p className="text-white/70 mb-6 text-lg">
              Join thousands of DJs, MCs, and independent artists on our music discovery platform. 
              Upload your tracks, get discovered by listeners worldwide, and earn from your streams.
            </p>
            
            {/* Revenue Split Highlight */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-400 font-bold text-2xl">70%</p>
                  <p className="text-white/60 text-sm">You Keep</p>
                </div>
                <div className="text-white/30 text-2xl">/</div>
                <div>
                  <p className="text-white/60 font-bold text-2xl">30%</p>
                  <p className="text-white/60 text-sm">Platform</p>
                </div>
              </div>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                Upload unlimited tracks
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                Get played on radio stations
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                Keep 70% of your earnings
              </li>
              <li className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                Track your analytics & payouts
              </li>
            </ul>

            <a 
              href="https://djsandmc.com" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-2xl transition-all hover:scale-105"
            >
              <Upload className="w-5 h-5" />
              Start Uploading
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Right - Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-green-500/20 to-purple-500/20 rounded-3xl p-8 border border-white/10">
              <div className="bg-black/60 rounded-2xl p-6 mb-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Your Track Title</p>
                    <p className="text-white/60 text-sm">Your Artist Name</p>
                  </div>
                </div>
                <div className="h-12 bg-white/5 rounded-lg flex items-end px-2 gap-1">
                  {[40, 65, 45, 80, 55, 70, 50, 85, 60, 75, 45, 90, 55, 70, 48].map((h, i) => (
                    <div 
                      key={i}
                      className="flex-1 bg-gradient-to-t from-green-500 to-green-400 rounded-t"
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-black/40 rounded-xl p-3">
                  <p className="text-2xl font-bold text-green-400">10K+</p>
                  <p className="text-white/50 text-xs">Plays</p>
                </div>
                <div className="bg-black/40 rounded-xl p-3">
                  <p className="text-2xl font-bold text-purple-400">500+</p>
                  <p className="text-white/50 text-xs">Fans</p>
                </div>
                <div className="bg-black/40 rounded-xl p-3">
                  <p className="text-2xl font-bold text-white">$250</p>
                  <p className="text-white/50 text-xs">Earned</p>
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
              YOU KEEP 70%
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


const PricingSection = ({ onOpenAuth }) => {
  const { user, loginWithGoogle } = useAuth();
  const [upgrading, setUpgrading] = useState(null);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const navigate = useNavigate();
  
  const plans = [
    { name: "Starter Channel", price: "19.99", description: "Launch your channel", features: ["Instant Setup — Go live in minutes", "Live streaming access", "AutoDJ system", "Basic branding", "100 listener slots", "Station URL (djsandmcmedia.com/station/you)", "Standard support"], popular: false, plan_id: "starter" },
    { name: "Pro Channel", price: "29.99", description: "For serious content creators", features: ["Instant Setup — Go live in minutes", "Everything in Starter", "Branded subdomain (you.djsandmcmedia.com)", "Custom domain setup available ($25)", "Higher listener capacity", "Priority streaming", "Advanced branding & analytics", "Optional onboarding call"], popular: true, plan_id: "professional" },
    { name: "BYOS Channel", price: "49.99", description: "Bring your own stream", features: ["Concierge Onboarding", "Bring your own stream", "Full white-label player", "Custom domain support included", "Full analytics dashboard", "Advanced widgets", "Sponsor area", "Premium support"], popular: false, plan_id: "byos" },
    { name: "Premium Channel", price: "79.99", description: "Launch & look professional", features: ["White-Glove Setup — We build it for you", "Everything in Pro", "Custom domain setup included free", "Featured placement on platform", "Homepage exposure", "Maximum listener capacity", "Priority support", "Growth-focused tools"], popular: false, plan_id: "premium" }
  ];

  const planOrder = { listener: -1, starter: 0, professional: 1, byos: 1, premium: 2 };
  const [showPaymentModal, setShowPaymentModal] = useState(null);

  const planPrices = { starter: "19.99", professional: "29.99", byos: "49.99", premium: "79.99" };

  const handlePlanAction = async (plan) => {
    if (!user) {
      onOpenAuth('register');
      return;
    }

    const effectivePlan = user.trial_active ? user.trial_plan : user.plan;
    if (effectivePlan === plan.plan_id) return;

    // Listeners must pay first — show payment modal instead of direct upgrade
    const isListener = user.plan === 'listener' || user.user_type === 'listener';
    if (isListener) {
      setShowPaymentModal(plan);
      // Notify admin about upgrade intent
      try {
        const token = localStorage.getItem('token');
        fetch(`${BACKEND_URL}/api/auth/upgrade-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ plan_name: plan.name, plan_id: plan.plan_id, price: plan.price })
        }).catch((err) => console.warn("Upgrade intent failed:", err.message));
      } catch (e) { console.warn("Upgrade intent error:", e.message); }
      return;
    }

    setUpgrading(plan.plan_id);
    setUpgradeMessage('');

    try {
      const token = localStorage.getItem('token');
      // If user is on starter and hasn't used trial, start a free trial
      const isTrialEligible = user.plan === 'starter' && !user.trial_used && !user.trial_active;
      const endpoint = isTrialEligible ? '/api/auth/start-trial' : '/api/auth/upgrade-plan';

      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan: plan.plan_id })
      });

      if (res.ok) {
        const data = await res.json();
        setUpgradeMessage(data.message);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const error = await res.json();
        setUpgradeMessage(error.detail || 'Failed to change plan');
      }
    } catch (err) {
      setUpgradeMessage('Failed to connect to server');
    } finally {
      setUpgrading(null);
    }
  };

  const getButtonText = (plan) => {
    if (!user) return 'Start Your Channel';
    const effectivePlan = user.trial_active ? user.trial_plan : user.plan;
    if (effectivePlan === plan.plan_id) {
      return user.trial_active ? `Trial (${plan.name})` : 'Current Plan';
    }
    const isListener = user.plan === 'listener' || user.user_type === 'listener';
    if (isListener) return 'Start Broadcasting';
    if (user.plan === 'starter' && !user.trial_used) return 'Start Free Trial';
    if ((planOrder[plan.plan_id] ?? 0) > (planOrder[effectivePlan] ?? -1)) return 'Upgrade';
    return 'Change Plan';
  };

  const isCurrentPlan = (plan) => {
    const effectivePlan = user?.trial_active ? user?.trial_plan : user?.plan;
    return effectivePlan === plan.plan_id;
  };

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-[#0a0a14] to-[#0f0f1a]" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">Simple, Transparent Pricing</h2>
        <p className="text-center text-white/60 mb-2 max-w-2xl mx-auto">Choose the plan that fits your channel needs.</p>
        <p className="text-center text-purple-400 font-medium mb-4 max-w-2xl mx-auto" data-testid="pricing-cta-line">Start your channel today — upgrade anytime as you grow.</p>
        
        <p className="text-center text-white/40 text-sm mb-8 max-w-2xl mx-auto" data-testid="pricing-disclaimer">DJsandMCMedia is a digital radio network. All channels operate under the DJsandMCMedia platform.</p>
        
        {/* Trial Message */}
        <div className="max-w-md mx-auto mb-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl px-6 py-5 text-center">
          <p className="text-purple-300 font-medium text-lg">Try any plan free for 7 days!</p>
          <p className="text-white/50 text-sm mt-1 mb-4">No credit card required to start your trial</p>
          <a href="/login?mode=register" className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white font-semibold px-6 py-2.5 rounded-xl transition text-sm" data-testid="trial-cta-btn">
            Start Your Free Trial
          </a>
        </div>
        
        {upgradeMessage && (
          <div className={`max-w-md mx-auto mb-8 px-4 py-3 rounded-xl text-center ${upgradeMessage.includes('Successfully') ? 'bg-green-500/20 border border-green-500/50 text-green-300' : 'bg-red-500/20 border border-red-500/50 text-red-300'}`}>
            {upgradeMessage}
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div key={index} className={`relative rounded-3xl p-6 transition-all duration-300 hover:scale-105 ${plan.popular ? "bg-gradient-to-b from-purple-500/20 to-pink-500/20 border-2 border-purple-500 shadow-lg shadow-purple-500/20" : plan.price === "19.99" || plan.price === "79.99" ? "bg-white/5 border border-purple-500/40 shadow-lg shadow-purple-500/15" : "bg-white/5 border border-white/10"} ${isCurrentPlan(plan) ? 'ring-2 ring-green-500' : ''}`} data-testid={`pricing-card-${index}`}>
              {plan.popular && <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">Best for Growth</span>}
              {isCurrentPlan(plan) && <span className="absolute -top-4 right-4 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Your Plan</span>}
              <h3 className="text-lg font-semibold text-white mb-1">{plan.name}</h3>
              <p className="text-white/50 text-xs mb-3">{plan.description}</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-3xl font-bold text-white">${plan.price}</span>
                <span className="text-white/50 text-sm">/month</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((feature, i) => {
                  const isInstant = feature.startsWith("Instant Setup") || feature.startsWith("White-Glove") || feature.startsWith("Concierge");
                  return (
                    <li key={i} className={`flex items-start gap-2 text-sm ${isInstant ? "text-emerald-400 font-semibold" : "text-white/80"}`}>
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isInstant ? "bg-emerald-500/20" : "bg-purple-500/20"}`}><span className={`text-[10px] ${isInstant ? "text-emerald-400" : "text-purple-400"}`}>{isInstant ? "⚡" : "✓"}</span></span>
                      <span className="leading-tight">{feature}</span>
                    </li>
                  );
                })}
              </ul>
              <button 
                onClick={() => handlePlanAction(plan)} 
                disabled={isCurrentPlan(plan) || upgrading === plan.plan_id}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                  isCurrentPlan(plan) 
                    ? 'bg-green-500/20 text-green-400 cursor-default' 
                    : "bg-purple-500 hover:bg-purple-600 text-white shadow-lg shadow-purple-500/25"
                } disabled:opacity-50`} 
                data-testid={`pricing-btn-${index}`}
              >
                {upgrading === plan.plan_id ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  getButtonText(plan)
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Domain Search */}
        <div className="mt-12 max-w-4xl mx-auto" data-testid="domain-search-section">
          <div className="text-center mb-5">
            <h3 className="text-xl font-bold text-white mb-1">Search Your Domain</h3>
            <p className="text-white/50 text-sm">Find the perfect domain for your station</p>
          </div>
          <DomainSearch />
        </div>

        {/* Custom Domain Setup Services */}
        <div className="mt-12 bg-gradient-to-br from-indigo-500/8 via-purple-500/5 to-transparent border border-indigo-500/15 rounded-3xl p-8 max-w-4xl mx-auto" data-testid="domain-services">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-white mb-1">Custom Domain Setup</h3>
            <p className="text-white/50 text-sm max-w-lg mx-auto">Look professional with your own domain. We handle DNS, SSL, and everything technical so you don't have to.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <div className="text-2xl font-bold text-white mb-1">$25</div>
              <div className="text-purple-400 text-xs font-semibold uppercase tracking-wide mb-2">Basic Setup</div>
              <p className="text-white/40 text-xs leading-relaxed">DNS instructions & configuration guide. You handle the domain purchase.</p>
            </div>
            <div className="bg-gradient-to-b from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-2xl p-5 text-center relative">
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">POPULAR</span>
              <div className="text-2xl font-bold text-white mb-1">$49</div>
              <div className="text-purple-400 text-xs font-semibold uppercase tracking-wide mb-2">Full Concierge Setup</div>
              <p className="text-white/40 text-xs leading-relaxed">We configure DNS, SSL, connect your station page & test everything.</p>
              <p className="text-white/30 text-[10px] mt-2 italic">Domain registration cost billed separately if not already owned.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-center">
              <div className="text-2xl font-bold text-white mb-1">$24.99<span className="text-sm text-white/40">/yr</span></div>
              <div className="text-purple-400 text-xs font-semibold uppercase tracking-wide mb-2">Domain Care Plan</div>
              <p className="text-white/40 text-xs leading-relaxed">Domain renewal, DNS management, SSL certificates & ongoing support.</p>
            </div>
          </div>
          <p className="text-center text-white/30 text-[11px] mt-4">Premium & BYOS plans include free domain setup. Pro plan: $25 one-time setup fee.</p>
        </div>

        {/* Pro Experience Add-on Section */}
        <div className="mt-16 pt-16 border-t border-white/10">
          <div className="text-center mb-10">
            <span className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-4 py-1 rounded-full mb-4">ADD-ON</span>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Pro Experience Upgrade</h3>
            <p className="text-white/60 max-w-xl mx-auto">Take your channel to the next level with premium tools designed for growth and engagement.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Monthly Subscription */}
            <div className="bg-gradient-to-b from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-3xl p-8 hover:scale-105 transition-all duration-300">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 text-yellow-400" />
                <h4 className="text-lg font-semibold text-white">Monthly Subscription</h4>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-bold text-white">$49.99</span>
                <span className="text-white/50">/month</span>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center"><span className="text-yellow-400 text-xs">✓</span></span>
                  Enhanced visual player
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center"><span className="text-yellow-400 text-xs">✓</span></span>
                  Live chat integration
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center"><span className="text-yellow-400 text-xs">✓</span></span>
                  Custom branding experience
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center"><span className="text-yellow-400 text-xs">✓</span></span>
                  Listener engagement tools
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-yellow-500/20 rounded-full flex items-center justify-center"><span className="text-yellow-400 text-xs">✓</span></span>
                  Priority feature access
                </li>
              </ul>
              
              {/* Payment Options */}
              <div className="space-y-3">
                {user ? (
                  <div data-testid="paypal-monthly-btn">
                    <PayPalButtons
                      style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay", height: 45 }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{ description: "DJsandMCMedia™ Pro Experience Upgrade - Monthly", amount: { value: "49.99", currency_code: "USD" } }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const details = await actions.order.capture();
                        const token = localStorage.getItem('token');
                        if (token) {
                          await fetch(`${BACKEND_URL}/api/payments/record`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ order_id: data.orderID, product_type: 'premium_monthly', amount: '49.99', payer_email: details.payer?.email_address, payer_name: details.payer?.name?.given_name })
                          });
                        }
                        alert('Payment successful! Your Pro Experience Upgrade is now active.');
                        window.location.reload();
                      }}
                      onError={(err) => console.error('PayPal error:', err)}
                    />
                  </div>
                ) : (
                  <button onClick={() => onOpenAuth('register')} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#0070ba] hover:bg-[#005ea6] text-white transition-all" data-testid="paypal-monthly-signup">
                    Upgrade Now
                  </button>
                )}
                <a 
                  href="https://cash.app/$garynuhayden1/49.99" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#00D632] hover:bg-[#00b82b] text-white transition-all"
                  data-testid="cashapp-monthly-btn"
                >
                  <span className="font-bold text-lg">$</span>
                  Pay $49.99 via Cash App
                </a>
                <a 
                  href="/features" 
                  className="block w-full py-2 rounded-xl text-center text-yellow-400 hover:text-yellow-300 text-sm transition-all"
                >
                  View Demo First
                </a>
              </div>
            </div>

            {/* White Label — High-Ticket Offer */}
            <div className="bg-gradient-to-b from-purple-500/10 to-pink-500/10 border-2 border-purple-500/50 rounded-3xl p-8 hover:scale-105 transition-all duration-300 relative shadow-lg shadow-purple-500/20">
              <span className="absolute -top-3 right-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold px-4 py-1 rounded-full">For Serious Creators & Brands</span>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-6 h-6 text-purple-400" />
                <h4 className="text-lg font-semibold text-white">White Label Experience</h4>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-white">$399.99</span>
                <span className="text-white/50">/month</span>
              </div>
              <p className="text-white/60 text-sm mb-4">Run your own branded experience powered by DJsandMCMedia.</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-purple-500/20 rounded-full flex items-center justify-center"><span className="text-purple-400 text-xs">✓</span></span>
                  Custom branded player
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-purple-500/20 rounded-full flex items-center justify-center"><span className="text-purple-400 text-xs">✓</span></span>
                  Dedicated experience layer
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-purple-500/20 rounded-full flex items-center justify-center"><span className="text-purple-400 text-xs">✓</span></span>
                  Advanced customization
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-purple-500/20 rounded-full flex items-center justify-center"><span className="text-purple-400 text-xs">✓</span></span>
                  Platform-level branding control
                </li>
              </ul>
              
              {/* Payment Options */}
              <div className="space-y-3">
                {user ? (
                  <div data-testid="paypal-lifetime-btn">
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{ description: "DJsandMCMedia™ White Label Experience - Monthly", amount: { value: "399.99", currency_code: "USD" } }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const details = await actions.order.capture();
                        const token = localStorage.getItem('token');
                        if (token) {
                          await fetch(`${BACKEND_URL}/api/payments/record`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ order_id: data.orderID, product_type: 'premium_lifetime', amount: '399.99', payer_email: details.payer?.email_address, payer_name: details.payer?.name?.given_name })
                          });
                        }
                        alert('Payment successful! Your White Label Experience is ready to configure.');
                        window.location.reload();
                      }}
                      onError={(err) => console.error('PayPal error:', err)}
                    />
                  </div>
                ) : (
                  <button onClick={() => onOpenAuth('register')} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#0070ba] hover:bg-[#005ea6] text-white transition-all" data-testid="paypal-lifetime-signup">
                    Apply Now
                  </button>
                )}
                <a 
                  href="https://cash.app/$garynuhayden1/399.99" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#00D632] hover:bg-[#00b82b] text-white transition-all"
                  data-testid="cashapp-lifetime-btn"
                >
                  <span className="font-bold text-lg">$</span>
                  Pay $399.99 via Cash App
                </a>
                <a 
                  href="#contact" 
                  className="block w-full py-2 rounded-xl text-center text-purple-400 hover:text-purple-300 text-sm transition-all"
                >
                  Have questions? Contact us
                </a>
              </div>
            </div>

            {/* On-Demand Add-On */}
            <div className="bg-gradient-to-b from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-3xl p-8 hover:scale-105 transition-all duration-300 relative">
              <span className="absolute -top-3 right-4 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Add-On</span>
              <div className="flex items-center gap-2 mb-4">
                <Music className="w-6 h-6 text-orange-400" />
                <h4 className="text-lg font-semibold text-white">On-Demand Library</h4>
              </div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-white">$99.99</span>
                <span className="text-white/50">/month</span>
              </div>
              <p className="text-white/40 text-xs mb-4">Create a content library your audience can access anytime.</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center"><span className="text-orange-400 text-xs">✓</span></span>
                  Upload &amp; manage tracks
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center"><span className="text-orange-400 text-xs">✓</span></span>
                  Listener playback access
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center"><span className="text-orange-400 text-xs">✓</span></span>
                  Build your content catalog
                </li>
                <li className="flex items-center gap-2 text-white/80 text-sm">
                  <span className="w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center"><span className="text-orange-400 text-xs">✓</span></span>
                  Monetization-ready structure
                </li>
              </ul>
              <div className="space-y-3">
                {user ? (
                  <div data-testid="paypal-ondemand-btn">
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{ description: "DJsandMCMedia™ On-Demand Add-On", amount: { value: "99.99", currency_code: "USD" } }]
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const details = await actions.order.capture();
                        const token = localStorage.getItem('token');
                        if (token) {
                          await fetch(`${BACKEND_URL}/api/payments/record`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ order_id: data.orderID, product_type: 'ondemand_addon', amount: '99.99', payer_email: details.payer?.email_address, payer_name: details.payer?.name?.given_name })
                          });
                        }
                        alert('On-Demand Add-On activated! Configure it in your Dashboard.');
                        window.location.reload();
                      }}
                      onError={(err) => console.error('PayPal error:', err)}
                    />
                  </div>
                ) : (
                  <button onClick={() => onOpenAuth('register')} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#0070ba] hover:bg-[#005ea6] text-white transition-all" data-testid="paypal-ondemand-signup">
                    Add Library
                  </button>
                )}
                <a 
                  href="https://cash.app/$garynuhayden1/99.99" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#00D632] hover:bg-[#00b82b] text-white transition-all"
                  data-testid="cashapp-ondemand-btn"
                >
                  <span className="font-bold text-lg">$</span>
                  Pay $99.99 via Cash App
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Listener Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="listener-payment-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Upgrade to {showPaymentModal.name}</h2>
              <button onClick={() => setShowPaymentModal(null)} className="text-white/50 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <p className="text-white/60 text-sm mb-4">Complete payment below to start broadcasting.</p>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/70 text-sm">{showPaymentModal.name}</span>
                <span className="text-white font-bold text-lg">${showPaymentModal.price}/mo</span>
              </div>
              <ul className="space-y-1.5">
                {showPaymentModal.features?.slice(0, 3).map((f, i) => (
                  <li key={i} className="text-white/50 text-xs flex items-center gap-1.5">
                    <span className="text-purple-400">&#10003;</span> {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3 mb-4">
              <a
                href={`https://paypal.me/wnuv985/${showPaymentModal.price}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#0070ba] hover:bg-[#005ea6] text-white transition-all"
                data-testid="listener-paypal-btn"
              >
                Pay ${showPaymentModal.price} via PayPal
              </a>
              <a
                href={`https://cash.app/$garynuhayden1/${showPaymentModal.price}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-[#00D632] hover:bg-[#00b82b] text-white transition-all"
                data-testid="listener-cashapp-btn"
              >
                <span className="font-bold text-lg">$</span>
                Pay ${showPaymentModal.price} via Cash App
              </a>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
              <p className="text-purple-300 text-xs text-center">
                Include your account email <strong className="text-white">{user?.email}</strong> in the payment note.
                Your plan will be activated after payment verification.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};


const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#0f0f1a] to-[#13112e]" data-testid="contact-section">
      <div className="max-w-2xl mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">Get In Touch</h2>
        <p className="text-center text-white/60 mb-12">Have questions? We'd love to hear from you.</p>
        {submitted ? (
          <div className="bg-green-500/20 border border-green-500/50 text-green-300 px-6 py-4 rounded-2xl text-center">Thanks for reaching out! We'll get back to you soon.</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white/70 text-sm mb-2">Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" placeholder="Your name" required data-testid="contact-name-input" />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition" placeholder="you@example.com" required data-testid="contact-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-white/70 text-sm mb-2">Message</label>
              <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-purple-500 transition h-32 resize-none" placeholder="How can we help?" required data-testid="contact-message-input" />
            </div>
            <button type="submit" className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-xl transition" data-testid="contact-submit-btn">Send Message</button>
          </form>
        )}
      </div>
    </section>
  );
};


const DomainSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearched(false);
    try {
      const r = await fetch(`${BACKEND_URL}/api/domains/search?q=${encodeURIComponent(q)}`);
      const d = await r.json();
      setResults(d.results || []);
      setSearched(true);
    } catch { setResults([]); }
    setSearching(false);
  };

  return (
    <div>
      <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mx-auto">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. thenuvibe, mystation..."
            className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder-white/25 focus:outline-none focus:border-purple-500/50 text-sm"
            data-testid="domain-search-input" />
        </div>
        <button type="submit" disabled={searching || !query.trim()}
          className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-6 py-3 rounded-xl transition text-sm disabled:opacity-50 flex items-center gap-2"
          data-testid="domain-search-btn">
          {searching ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Globe className="w-4 h-4" />}
          Search
        </button>
      </form>
      {searched && results.length > 0 && (
        <div className="mt-4 max-w-lg mx-auto bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden" data-testid="domain-results">
          {results.map((r, i) => (
            <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? "border-t border-white/[0.03]" : ""}`}>
              <span className="text-white text-sm font-mono">{r.domain}</span>
              {r.available ? (
                <span className="text-emerald-400 text-xs font-semibold flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Available
                </span>
              ) : (
                <span className="text-red-400/60 text-xs flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Taken
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {searched && results.length === 0 && (
        <p className="text-center text-white/30 text-xs mt-3">No results found. Try a different name.</p>
      )}
    </div>
  );
};




const Home = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const location = useLocation();

  // Scroll to hash section when arriving from another page
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [location.hash]);

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a14]">
      <SEO
        title="Your Channel. Your Brand. Your Platform."
        description="The #1 platform for DJs, MCs, and Podcasters to launch and manage branded online radio stations. White-label players, AutoDJ, live video, analytics, and more."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "DJsandMCMedia",
          "url": window.location.origin,
          "description": "Radio station hosting platform for DJs, MCs, and Podcasters",
          "sameAs": ["https://djsandmc.com"]
        }}
      />
      <Header onOpenAuth={openAuth} />
      <DJsAndMCMediaHero onOpenAuth={openAuth} />
      <FeaturesSection />
      <TestimonialsSection />
      <ArtistsSection />
      <PricingSection onOpenAuth={openAuth} />
      <ContactSection />
      <Footer />
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} initialMode={authMode} />
    </div>
  );
};

export default Home;
